import React, {useState} from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Card from 'src/components-new/layout/Card'
import EmptyState from 'src/components-new/layout/EmptyState'
import {Product, ErodedPackage} from 'src/types/types'
import {beneficiaryOptions} from 'src/constants/constants'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'

interface PricingTableProps {
  erodedPackage: ErodedPackage
}

function PricingTable({erodedPackage}: PricingTableProps): JSX.Element {
  const erodedHourKeys = erodedPackage.erodedHour ? Object.keys(erodedPackage.erodedHour) : []
  const erodedEuroKeys = erodedPackage.erodedEuro ? Object.keys(erodedPackage.erodedEuro) : []
  const erodedPerfKeys = erodedPackage.erodedPerformances ? Object.keys(erodedPackage.erodedPerformances) : []

  const columns = beneficiaryOptions.filter(opt =>
    erodedHourKeys.includes(opt.value) ||
    erodedEuroKeys.includes(opt.value) ||
    erodedPerfKeys.includes(opt.value)
  )

  const rows: Record<string, any>[] = []
  if (erodedPackage.erodedHour) rows.push({key: 'Eroso ore', ...erodedPackage.erodedHour})
  if (erodedPackage.erodedEuro) rows.push({key: 'Eroso euro', ...erodedPackage.erodedEuro})
  if (erodedPackage.erodedPerformances) rows.push({key: 'Eroso prestazioni', ...erodedPackage.erodedPerformances})

  return (
    <Box sx={{overflowX: 'auto', mb: 2}}>
      <Typography variant="body2" sx={{fontWeight: 600, mb: 1}}>
        {erodedPackage.name}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{fontWeight: 600, color: NEUTRAL_SCALE[700]}}>{'Servizio'}</TableCell>
            {columns.map(col => (
              <TableCell key={col.value} sx={{fontWeight: 600, color: NEUTRAL_SCALE[700]}}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.key}>
              <TableCell>{row.key}</TableCell>
              {columns.map(col => (
                <TableCell key={col.value}>{row[col.value] ?? '—'}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

interface PricingSectionProps {
  products: Product[]
}

export default function PricingSection({products}: PricingSectionProps): JSX.Element {
  const [expanded, setExpanded] = useState<string | false>(false)

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  return (
    <Card title="Prodotti attivi">
      {products.length === 0 ? (
        <EmptyState title="Nessun risultato" description="Nessun prodotto attivo" />
      ) : (
        products.map((product, index) => (
          <Accordion
            key={product.name + index}
            expanded={expanded === product.name + index}
            onChange={handleChange(product.name + index)}
            disableGutters
            elevation={0}
            sx={{
              border: `1px solid ${NEUTRAL_SCALE[200]}`,
              borderRadius: '8px !important',
              mb: 1,
              '&:before': {display: 'none'},
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{fontWeight: 600}}>
                {product.name}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {product.packages.map((pkg, i) => (
                <PricingTable key={pkg._id + i} erodedPackage={pkg} />
              ))}
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Card>
  )
}
